import { Backdrop, Box, CircularProgress, TextField, useTheme } from '@mui/material';
import Header from '../../components/Header';
import { useTranslation } from '../../hooks/useTranslation';
import { useCallback, useEffect, useState } from 'react';
import { tokens } from '../../theme';
import { formatCpfCnpj } from '../../utils';
import EditIcon from '@mui/icons-material/Edit';
import CustomTable from '../../components/CustomTable';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import api from '../../services/api';

const CustomerList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const fetchClients = useCallback(
    async (term = '') => {
      try {
        setLoadingList(true);
        const cleanTaxvar = term.replace(/\D/g, '');
        const { data } = await api.get(`/v1/customer/list?taxvar=${cleanTaxvar}`);
        setClients(data?.list ?? []);
      } catch (error) {
        console.error(t('messages.errorFetchingData'), error);
      } finally {
        setLoadingList(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const columns = [
    {
      field: 'full_name',
      headerName: t('customers.name'),
      render: (value) => (
        <span
          style={{
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'uppercase',
          }}
        >
          {value}
        </span>
      ),
    },
    {
      field: 'taxvar',
      headerName: t('customers.cpfCnpj'),
      render: (value) => formatCpfCnpj(value),
    },
    {
      field: 'completeRegistration',
      headerName: t('customers.completeRegistration'),
      render: () => 'Não',
    },
  ];

  const actions = [
    {
      icon: <EditIcon sx={{ fontSize: 18 }} />,
      tooltip: t('buttons.edit'),
      onClick: (client) => navigate(`${client.id}/edit`),
    },
  ];

  if (loadingList) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box m="20px">
      <Header title={t('titles.createClient')} subtitle={t('titles.createClientSubtitle')} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        {/* Busca */}
        <Box sx={{ display: 'flex', gap: 2, height: '100%', width: '100%' }}>
          <TextField
            fullWidth
            placeholder={t('forms.cpfCnpj')}
            value={formatCpfCnpj(searchTerm)}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              if (value === '') fetchClients();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') fetchClients(searchTerm);
            }}
            variant="outlined"
            InputProps={{
              sx: {
                backgroundColor: '#fff',
                borderRadius: '4px',
                color: colors.grey[400],
                '& fieldset': { borderColor: colors.primary[400] },
                '&:hover fieldset': { borderColor: colors.greenAccent[400] },
                '&.Mui-focused fieldset': { borderColor: colors.greenAccent[400] },
              },
            }}
            sx={{ maxWidth: { xs: '100%', md: '400px' } }}
          />

          <Button
            fullWidth={false}
            onClick={() => fetchClients(searchTerm)}
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            {t('buttons.consult')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex' }}>
          {/* Ocultado para utilizar no futuro */}
          {/* <Button
              variant="contained"
              sx={{
                backgroundColor: '#fff',
                border: `2px solid ${colors.greenAccent[500]}`,
                color: colors.greenAccent[500],
                fontWeight: 'bold',
                borderRadius: '16px',
                '&:hover': {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
              onClick={() => setOpen(true)}
            >
              <CloudUploadIcon fontSize="large" />
            </Button> */}

          {/* Cadastrar */}
          <Button
            onClick={() => navigate('new')}
            variant="contained"
            sx={{
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {t('buttons.registerWithoutConsult')}
          </Button>
        </Box>
      </Box>

      <CustomTable
        columns={columns}
        rows={clients}
        actions={actions}
        emptyMessage={t('messages.noClientsFound')}
      />
    </Box>
  );
};

export default CustomerList;
